import CommitRoundedIcon from '@mui/icons-material/CommitRounded';
import { Box, CircularProgress, Fade, Grid, Paper, Typography, useMediaQuery } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import getRevenueData from '../../../../../../lib/stock/_getRevenueData';
import { dataConstants } from '../../../../../constants';
import eventObserver from '../../../../../utils/EventObserver';
import { parseTabData, useFetchTabData } from '../../../../../utils/helpers';
import { convertDate_MDY } from '../../../../../utils/time';
import Chart from '../../../../_common/VisxChart/AreaChart';
import RevenuesTable from './RevenuesTable';

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? 'rgb(10, 25, 41)' : '#fff',
  ...theme.typography.body2,
  height: '100%',
  color: theme.palette.text.secondary,
  boxShadow:
    theme.palette.mode === 'dark'
      ? 0
      : '0 0.5px 1px rgb(255 255 255 / 20%), 0 2px 3px rgb(255 255 255 / 10%)',
  border:
    theme.palette.mode === 'dark'
      ? '1px solid rgb(19, 47, 76)'
      : '1px solid #eae9ee',
  borderRadius: '7px',
  '& .Item-Head': {
    background: '#fbfbfc',
    borderBottom: '1px solid #eae9ee',
    marginBottom: 0,
    padding: theme.spacing(1, 2),
    borderRadius: '7px 7px 0 0',
    '& a': { textDecoration: 'none', color: '#333', fontWeight: 600 },
    '& .option-title': { fontWeight: 600, color: '#13113c', fontSize: '.9rem' },
  },
  '& .Item-Body': { padding: theme.spacing(1) },
}));

const MOBILE_CHART_SETTING = {
  themeName: 'Default',
  aspRatio: 2,
  chartMargin: { bottom: 30, right: 60 },
  xaxis: { setting: { label: { fs: '40px' } } },
  yaxis: { setting: { label: { fs: '40px' } } },
  grid: { line: { stroke: '#999999', dashArray: '1,1' } },
};

const getFirstGroup = (data) => {
  if (!data) return null;
  const groupedData = data.reduce((result, item) => {
    (result[item.group] = result[item.group] || []).push(item);
    return result;
  }, {});
  const sortOrder = ['PROD', 'BUS', 'GEO', 'TOTAL'];
  const [firstGroup] = sortOrder
    .map((group) => (groupedData[group] || [])[0])
    .filter(Boolean);
  return firstGroup;
};

const RevenuesBreakup = ({ tabData, info, divideColumnsBy, revenues_data }) => {
  const theme = useTheme();
  const router = useRouter();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const latestRequestRef = useRef('');
  const isMountedRef = useRef(true);

  const ticker = (router.query.ticker?.toUpperCase()) || info?.ticker || '';

  const [seriesCode, setSeriesCode] = useState('');
  const [seriesName, setSeriesName] = useState('');
  const [newMetricData, setNewMetricData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  const firstGroup = useMemo(() => getFirstGroup(revenues_data), [revenues_data]);

  const metricsChartsData = useMemo(() => {
    const raw = tabData?.find((d) => d.section_code === 'revenue_break');
    return raw ? parseTabData(raw) : null;
  }, [tabData]);

  const { data: metric_chart_item, loading: metLoading } = useFetchTabData(
    info?.ticker,
    'revenue_break_chart',
    'NEW_SUM_OD',
    'RBC'
  );

  const chartItem = metric_chart_item?.tabData ?? null;
  const chartSeriesData = chartItem?.series ?? null;

  const transformedData = useMemo(() => {
    if (!newMetricData) return [];
    return newMetricData
      .filter((d) => d.value !== null)
      .map((item) => ({ date: new Date(item.recordDate), value: item.value }));
  }, [newMetricData]);

  const chartData = useMemo(() => {
    if (!transformedData.length || !chartItem || !seriesCode) return null;
    const currentSeriesCode = seriesCode.replace(/XXXXX/g, ticker);
    return {
      ...chartItem,
      id: currentSeriesCode,
      chartSetting: isMobile
        ? MOBILE_CHART_SETTING
        : { ...(chartItem.chartSetting || {}), aspRatio: 3 },
      series: chartSeriesData?.map((d) => ({ ...d, yformula: currentSeriesCode })) || [],
      xSeries: chartItem.x_series,
      ySeries: currentSeriesCode,
      toDate: '',
      series_data: transformedData,
      stock_data: [],
    };
  }, [transformedData, chartItem, chartSeriesData, seriesCode, ticker, isMobile]);

  useEffect(() => {
    if (chartData && seriesCode) {
      eventObserver.notify('reDraw', {
        chart: chartData,
        chartId: `svg-undefined-${seriesCode}`,
        chartState: dataConstants.CHART_STATE.VIEW,
      });
    }
  }, [chartData, seriesCode]);

  const getHistChartData = useCallback(async (code, name) => {
    if (!code) return;
    latestRequestRef.current = code;
    setLoading(true);
    const convertedCode = code.replace(/[\.\@\|\$\^\:]/g, '_');

    try {
      const data = await getRevenueData(code);
      if (latestRequestRef.current !== code || !isMountedRef.current) return;

      setSeriesCode(code);
      setSeriesName(name);
      const metricArray = data?.[convertedCode] || [];
      const sortedData = [...metricArray].sort(
        (a, b) => new Date(b.recordDate) - new Date(a.recordDate)
      );
      setNewMetricData(sortedData);
    } catch (error) {
      if (latestRequestRef.current === code && isMountedRef.current) {
        console.error('Error fetching revenues data:', error);
      }
    } finally {
      if (latestRequestRef.current === code && isMountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (firstGroup?.seriescode && firstGroup?.desc) {
      getHistChartData(firstGroup.seriescode, firstGroup.desc);
    }
  }, [firstGroup, getHistChartData]);

  if (!metricsChartsData || !newMetricData) return null;

  return (
    <Grid size={{ xs: 12, sm: 12 }}>
      <Item className="crisischart">
        <Box
          className="Item-Head"
          sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', mb: 0.5, px: 0.5 }}
        >
          <Box
            component="div"
            sx={{
              '& .api-link': { color: '#174c86 !important', fontWeight: '500 !important' },
              '& .api-link:hover': { textDecoration: 'underline !important' },
            }}
          >
            <Box component="div" sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography component="p" className="option-title" sx={{ mr: 2, whiteSpace: 'nowrap' }}>
                {metricsChartsData?.section_name}
              </Typography>
              {firstGroup?.recordDate && (
                <Typography variant="caption" component="div" className="option-desc" sx={{ fontSize: '.77rem' }}>
                  As of: {convertDate_MDY(firstGroup.recordDate)}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        <Box sx={{ width: '100%', my: 0 }}>
          <Grid container spacing={1.2}>
            <Grid size={{ xs: 12, sm: 12, md: divideColumnsBy || 12 }}>
              <Box component="div" className="option-content">
                {!loading && !metLoading && newMetricData && chartData ? (
                  <Box className="wide-chart">
                    <Box
                      component="div"
                      className="option-content Item-Body"
                      sx={{ width: '100%', height: '100%', mb: 1, pb: '0 !important' }}
                    >
                      <Chart
                        data={chartData}
                        _height={167}
                        _margin={{ top: 10, right: 35, bottom: 20, left: 15 }}
                      />
                    </Box>
                    <Box
                      component="div"
                      sx={{
                        borderRadius: '4px',
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'center',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                      }}
                    >
                      {/* FIX #1: use stable key from data, not getRandomStr */}
                      {chartData.series?.map((d) => (
                        <Box
                          key={d.yformula || d.legend?.label}
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            mr: 1.5,
                            mb: { md: 1, xs: 1 },
                          }}
                        >
                          <CommitRoundedIcon
                            sx={{
                              mr: 0.7,
                              width: '1em !important',
                              fontSize: '1.28rem !important',
                              color: d?.setting?.line?.stroke,
                            }}
                          />
                          {/* FIX #17: variant="body2" instead of invalid "p" */}
                          <Typography variant="body2" sx={{ fontSize: '.75rem', fontWeight: 600 }}>
                            {d?.legend?.label || seriesName}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ) : (
                  <Box
                    className="chart-loader"
                    sx={{
                      height: { md: '13.22rem', sm: '11rem', xs: '11rem' },
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Fade
                      in={loading || metLoading}
                      style={{ transitionDelay: loading || metLoading ? '800ms' : '0ms' }}
                      unmountOnExit
                    >
                      <CircularProgress color="inherit" />
                    </Fade>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>

        {revenues_data && revenues_data.length > 0 && (
          <RevenuesTable
            revenues_data={revenues_data}
            getHistChartData={getHistChartData}
            seriesName={seriesName}
            seriesCode={seriesCode}
          />
        )}
      </Item>
    </Grid>
  );
};

export default React.memo(RevenuesBreakup);
